import { useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import { documentKindLabels, runSuiteLabels } from '../field-object-labels';

import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import './field-objects.css';

import type { FieldPanelProps } from './field-types';
import type { FieldActorId, FieldCommand } from '@mio/shared';

type DeliveryKind = Extract<FieldCommand['action'], { type: 'delivery.record' }>['kind'];
const kinds: DeliveryKind[] = [
  'decision',
  'scope',
  'runbook',
  'test_plan',
  'report',
  'postmortem',
  'handoff',
];

export function FieldDeliveryForm({
  snapshot,
  busy,
  onAction,
  open,
  onClose,
}: FieldPanelProps & {
  open?: boolean;
  onClose?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = open ?? expanded;
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [kind, setKind] = useState<DeliveryKind>('decision');
  const [recipients, setRecipients] = useState<FieldActorId[]>([]);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const sources = [
    ...(snapshot.discovery?.decisions.map((decision) => ({
      id: decision.id,
      label: `Decision · ${decision.title} · v${decision.version}`,
    })) ?? []),
    ...(snapshot.discovery?.findings.map((finding) => ({
      id: finding.id,
      label: `Finding · ${finding.title}`,
    })) ?? []),
    ...snapshot.prs.map((pr) => ({
      id: pr.id,
      label: `${pr.title} · ${pr.revision.slice(0, 12)}`,
    })),
    ...snapshot.documents.map((document) => ({
      id: document.id,
      label: `${document.title} · v${document.version}`,
    })),
    ...snapshot.incidents.map((incident) => ({
      id: incident.id,
      label: `Incident · ${incident.title} · ${incident.status}`,
    })),
    ...snapshot.runs.map((run) => ({
      id: run.id,
      label: `Run · ${runSuiteLabels[run.suite]} · ${snapshot.prs.find((pr) => pr.id === run.prId)?.title ?? run.prId} · ${run.revision.slice(0, 12)} · ${run.status}${run.trusted ? ' · verified' : ' · unverified'}`,
    })),
  ];
  return (
    <section className="field-delivery-composer">
      {open === undefined ? (
        <Button variant="secondary" onClick={() => setExpanded(!expanded)} aria-expanded={visible}>
          New delivery
        </Button>
      ) : null}
      <form
        className="field-object-form space-y-4"
        hidden={!visible}
        onSubmit={(event) => {
          event.preventDefault();
          setSaved(false);
          void onAction({
            type: 'delivery.record',
            title,
            text,
            kind,
            sourceIds,
            actorIds: recipients,
          }).then((success) => {
            if (success) {
              setTitle('');
              setText('');
              setSourceIds([]);
              setSaved(true);
            }
          });
        }}
      >
        <h3 className="text-base font-semibold">New delivery</h3>
        <p className="text-fg-muted text-sm leading-6">
          Record a decision, runbook, report, or handoff. Save it privately or select the colleagues
          who should receive this version.
        </p>
        <div className="field-object-form-grid">
          <div>
            <label htmlFor="delivery-title" className={FIELD_LABEL}>
              Title of your work
            </label>
            <input
              id="delivery-title"
              className={FIELD_INPUT}
              value={title}
              maxLength={200}
              required
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="delivery-kind" className={FIELD_LABEL}>
              Document type
            </label>
            <SelectField
              id="delivery-kind"
              value={kind}
              onValueChange={(nextValue) => {
                const next = kinds.find((item) => item === nextValue);
                if (next) setKind(next);
              }}
              options={[
                ...(kinds.map((item) => ({
                  value: item,
                  label: documentKindLabels[item] ?? item,
                })) ?? []),
              ]}
            />
          </div>
        </div>
        <div>
          <label htmlFor="delivery-text" className={FIELD_LABEL}>
            Content (Markdown supported in the download)
          </label>
          <textarea
            id="delivery-text"
            rows={8}
            className={FIELD_INPUT}
            value={text}
            maxLength={64000}
            required
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        {sources.length ? (
          <fieldset>
            <legend className={FIELD_LABEL}>Evidence references</legend>
            <div className="max-h-40 space-y-2 overflow-y-auto">
              {sources.map((source) => (
                <label key={source.id} className="field-object-choice">
                  <input
                    type="checkbox"
                    checked={sourceIds.includes(source.id)}
                    onChange={(event) =>
                      setSourceIds((previous) =>
                        event.target.checked
                          ? [...previous, source.id]
                          : previous.filter((id) => id !== source.id),
                      )
                    }
                  />
                  {source.label}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        <fieldset>
          <legend className={FIELD_LABEL}>Send to (leave empty to save without sharing)</legend>
          <div className="flex flex-wrap gap-4">
            {snapshot.actors.map((actor) => (
              <label key={actor.id} className="field-object-choice">
                <input
                  type="checkbox"
                  checked={recipients.includes(actor.id)}
                  onChange={(event) =>
                    setRecipients((previous) =>
                      event.target.checked
                        ? [...previous, actor.id]
                        : previous.filter((id) => id !== actor.id),
                    )
                  }
                />
                {actor.name.split(' ')[0]}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            disabled={busy || !title.trim() || !text.trim() || sourceIds.length > 30}
          >
            {recipients.length ? 'Save and send to selected colleagues' : 'Save my document'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setExpanded(false);
              onClose?.();
            }}
          >
            Close
          </Button>
        </div>
        {saved ? (
          <p role="status" className="text-success text-sm">
            Your document was recorded.
          </p>
        ) : null}
      </form>
    </section>
  );
}
